/**
 * Collection filter behaviour
 * ---------------------------------------------------------------------------
 * Three fixes for the filter sidebar, which is rebuilt from the Lambda response
 * on every refinement, sort and page change (see LoadCollectionPage in
 * assets/foundrylighting.js).
 *
 * 1. Suppress the theme's native faceting.
 *    Theme.js defines a `facet-link` custom element (the "Clear all" link and
 *    the active-facet chips) whose own click handler dispatches `facet:update`,
 *    and a document-level listener that answers it by fetching the collection
 *    section from Shopify and swapping it into the page. On this store the
 *    server-rendered grid is deliberately empty, so that swap races the Lambda
 *    response and intermittently leaves the grid and sidebar blank. jQuery's
 *    `unbind` in BindFacetLinks can't remove the custom element's own native
 *    listener, so the event is cancelled here instead, before it is handled.
 *
 * 2. Drop a trailing slash from collection URLs.
 *    LoadCollectionPage posts window.location.pathname as collection_handle.
 *    "/collections/marie-flanigan/" leaves an empty handle after the last
 *    slash, and the endpoint answers with the whole catalogue instead of the
 *    collection. Normalised here before the theme's document-ready runs.
 *
 * 3. Remember which filter groups the shopper opened.
 *    The rebuild throws away open groups and expanded "Show More" lists,
 *    including the group the shopper just refined in. Both are restored from
 *    sessionStorage on every re-render.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // 1. Cancel the theme's native facet rendering
  // ---------------------------------------------------------------------------

  // Capture phase on document runs ahead of theme.js's own document-level
  // listener, so the section fetch never starts.
  document.addEventListener(
    'facet:update',
    function (event) {
      event.stopImmediatePropagation();
      event.preventDefault();
    },
    true
  );

  // ---------------------------------------------------------------------------
  // 2. Normalise a trailing slash on collection URLs
  // ---------------------------------------------------------------------------

  (function normalizePath() {
    var path = window.location.pathname;

    if (!/^\/collections\/.+\/$/.test(path)) return;

    try {
      window.history.replaceState(
        window.history.state,
        '',
        path.replace(/\/+$/, '') + window.location.search + window.location.hash
      );
    } catch (error) {
      /* nothing to do — the page still renders, just against the wrong handle */
    }
  })();

  // ---------------------------------------------------------------------------
  // 3. Remember open filter groups and expanded "Show More" lists
  // ---------------------------------------------------------------------------

  var STORAGE_KEY = 'fl:facet-state';
  var ROOTS = '.facets-sidebar, #facets-drawer-form';
  var applying = false;

  function readState() {
    try {
      return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY)) || {};
    } catch (error) {
      return {};
    }
  }

  function writeState(state) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      /* private mode, storage full, etc. — state simply isn't remembered */
    }
  }

  function groupName(details) {
    var toggle = details.querySelector('summary .accordion__toggle');
    if (!toggle) return '';
    return toggle.textContent.replace(/\s+/g, ' ').trim().toUpperCase();
  }

  function remember(key, value) {
    var state = readState();
    state[key] = value;
    writeState(state);
  }

  function apply(root) {
    var state = readState();
    applying = true;

    root.querySelectorAll('details.accordion').forEach(function (details) {
      var name = groupName(details);
      if (!name) return;

      if (Object.prototype.hasOwnProperty.call(state, 'open:' + name)) {
        var shouldBeOpen = state['open:' + name];

        if (details.open !== shouldBeOpen) {
          details.open = shouldBeOpen;
          details.setAttribute('aria-expanded', shouldBeOpen ? 'true' : 'false');
        }
      }

      if (state['more:' + name]) {
        var button = details.querySelector('.show-more-less');

        if (button && button.textContent.trim() === 'Show More') {
          button.textContent = 'Show Less';
          details.querySelectorAll('.facet-filters .checkbox-control.hide').forEach(function (control) {
            control.classList.remove('hide');
            control.classList.add('open');
          });
        }
      }
    });

    applying = false;
  }

  function bind(root) {
    root.querySelectorAll('details.accordion').forEach(function (details) {
      if (details.dataset.facetStateBound === 'true') return;
      details.dataset.facetStateBound = 'true';

      details.addEventListener('toggle', function () {
        if (applying) return;
        var name = groupName(details);
        if (name) remember('open:' + name, details.open);
      });
    });
  }

  var timer = null;

  function schedule() {
    // setTimeout rather than requestAnimationFrame: the sidebar is rebuilt by an
    // AJAX callback that can land while the tab is backgrounded, where rAF is
    // throttled and the restore would never run.
    if (timer) return;

    timer = window.setTimeout(function () {
      timer = null;

      document.querySelectorAll(ROOTS).forEach(function (root) {
        bind(root);
        apply(root);
      });
    }, 0);
  }

  var attempts = 0;

  function boot() {
    var roots = document.querySelectorAll(ROOTS);

    if (!roots.length) {
      // The sidebar is rendered by the collection section; retry briefly in case
      // this script runs first.
      if (attempts++ < 20) window.setTimeout(boot, 250);
      return;
    }

    roots.forEach(function (root) {
      if (root.dataset.facetStateObserved === 'true') return;
      root.dataset.facetStateObserved = 'true';
      new MutationObserver(schedule).observe(root, { childList: true, subtree: true });
    });

    schedule();
  }

  // "Show More" is re-bound by the theme on every render, so read the intent in
  // the capture phase, before the theme's own handler flips the label.
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      var button = target && target.closest ? target.closest('.show-more-less') : null;
      if (!button) return;

      var details = button.closest('details.accordion');
      var name = details ? groupName(details) : '';
      if (!name) return;

      remember('more:' + name, button.textContent.trim() === 'Show More');
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
